import { Link } from "react-router-dom";
import { Target, Sparkles, Rocket, Wallet, Lightbulb, GraduationCap, ArrowRight, Eye, Users, Wrench, Trophy } from "lucide-react";

const FRAMEWORK = [
  {
    icon: Sparkles,
    color: "#06B6D4",
    title: "SEAA Enrichment",
    subtitle: "Science · Engineering · Athletics · Arts",
    text: "Our integrated enrichment model weaves four disciplines into real-world learning pathways — so students explore, create, and discover their strengths.",
  },
  {
    icon: Target,
    color: "#F97316",
    title: "Performance Readiness System",
    subtitle: "The foundation of every program",
    text: "Every LP Foundation program begins with structured performance readiness — building focus, self-discipline, communication, teamwork, and confidence so students are prepared to engage.",
  },
  {
    icon: GraduationCap,
    color: "#7C3AED",
    title: "Youth Development Model",
    subtitle: "The whole child",
    text: "We support students mentally, emotionally, and academically — embedding academic skills into the things young people already care about, turning passion into performance.",
  },
  {
    icon: Rocket,
    color: "#14B8A6",
    title: "Workforce Readiness",
    subtitle: "Real-world exposure",
    text: "Through hands-on industry experiences in aviation, music production, digital finance, film, and design, students gain early exposure to careers and the skills employers value.",
  },
  {
    icon: Wallet,
    color: "#22C55E",
    title: "Financial Literacy",
    subtitle: "Confidence with money & technology",
    text: "From digital finance to responsible financial thinking, students build the literacy and habits that lead to long-term opportunity and independence.",
  },
  {
    icon: Lightbulb,
    color: "#1E40AF",
    title: "Entrepreneurship",
    subtitle: "Ideas into impact",
    text: "Students learn to design, build, and present — developing creative problem-solving, brand thinking, and the confidence to turn ideas into something real.",
  },
];

const APPROACH = [
  { icon: Eye, color: "#2563EB", step: "See", text: "Students are exposed to real careers, tools, and possibilities — sparking curiosity and showing what's achievable." },
  { icon: Users, color: "#14B8A6", step: "Engage", text: "Hands-on participation and structured performance readiness keep students focused, involved, and confident." },
  { icon: Wrench, color: "#7C3AED", step: "Apply", text: "Students put skills into practice through projects that connect academics to the real world." },
  { icon: Trophy, color: "#F97316", step: "Achieve", text: "Growth becomes measurable — building capability, character, and momentum toward future success." },
];

export default function Framework() {
  return (
    <div data-testid="framework-page">
      <section className="bg-brandnavy">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-24">
          <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Our Framework</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-black tracking-tight text-white max-w-3xl">
            A structured model that turns potential into performance.
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
            Everything we do is structured, measurable, and community-centered. Early exposure builds confidence. Confidence
            builds capability. Capability builds futures.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 grid gap-6 md:grid-cols-2">
        {FRAMEWORK.map((f) => (
          <div key={f.title} data-testid={`framework-${f.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
            <span className="grid h-14 w-14 place-items-center rounded-2xl" style={{ background: `${f.color}1a`, color: f.color }}>
              <f.icon className="h-7 w-7" />
            </span>
            <h2 className="mt-5 font-display text-2xl font-black text-brandnavy">{f.title}</h2>
            <p className="mt-1 text-sm font-semibold uppercase tracking-wide" style={{ color: f.color }}>{f.subtitle}</p>
            <p className="mt-4 text-slate-600 leading-relaxed">{f.text}</p>
          </div>
        ))}
      </section>

      <section className="bg-brandnavy py-20">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-2xl">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-accent">Our Learning Approach</span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-black tracking-tight text-white">
              See · Engage · Apply · Achieve
            </h2>
            <p className="mt-4 text-lg text-slate-300">
              Every LP experience follows a simple, proven progression that turns curiosity into capability.
            </p>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {APPROACH.map((a, i) => (
              <div key={a.step} data-testid={`approach-${a.step.toLowerCase()}`} className="relative rounded-3xl bg-white/5 border border-white/10 p-7 transition-all hover:-translate-y-1 hover:bg-white/10">
                <span className="grid h-14 w-14 place-items-center rounded-2xl" style={{ background: `${a.color}26`, color: a.color }}>
                  <a.icon className="h-7 w-7" />
                </span>
                <div className="mt-5 flex items-center gap-2">
                  <span className="font-display text-sm font-bold" style={{ color: a.color }}>{String(i + 1).padStart(2, "0")}</span>
                  <h3 className="font-display text-xl font-black text-white">{a.step}</h3>
                </div>
                <p className="mt-2 text-sm text-slate-300 leading-relaxed">{a.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-orange-50">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 py-16 text-center">
          <h2 className="font-display text-2xl sm:text-3xl font-black text-brandnavy">See the framework in action.</h2>
          <p className="mt-3 text-slate-600">Explore the six SEAA programs built on this model.</p>
          <Link to="/programs" data-testid="framework-programs-cta" className="mt-7 inline-flex items-center gap-2 rounded-full bg-brandnavy px-7 py-3.5 font-semibold text-white transition hover:-translate-y-0.5">
            View Programs <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
